// Pure helper functions used by Enquiries.jsx and its sub-components.
// None of these depend on component state — they're plain data transforms,
// so they're kept separate to keep the container component focused on
// wiring state to the UI.

export const STAGE_HISTORY_PAGE_SIZE = 10;

// The card list now comes from a lightweight, paginated endpoint
// (`/enquiry-stage-history/employee/details`) that returns a flat array of
// summaries — one per card — instead of the old array-of-stages-with-nested-
// histories shape. <EnquiryStageHistory /> still expects that older grouped
// shape, so this regroups the flat list by stage without touching that
// component. Full per-card detail (real checklist ids/completion, product
// specs, remarks) is fetched lazily by <KanbanCard /> when a card is
// expanded, via `/enquiry-stage-history/employee/{id}`.
export const mapLightweightRecordToHistoryItem = (record) => ({
  id: record.id,
  enquiryId: record.enquiryId,
  leadName: record.leadName,
  clientName: record.clientName,
  assignedTo: record.assignedTo,
  priority: record.priority,
  dueAt: record.dueAt,
  createdAt: record.createdAt,
  updatedAt: record.updatedAt,
  endedAt: record.endedAt,
  notes: record.notes,
  remarks: "",
  checkListHistories: [],
  // The lightweight endpoint sends pre-computed counts (no checklist detail
  // yet, that's fetched lazily per-card) — carry them through as-is instead
  // of letting downstream code recompute from the empty checkListHistories
  // above, which would incorrectly report 0 completed.
  completedTasks: record.completedTasks,
  totalTasks: record.totalTasks,
  productEnquiryHistories: (record.productHistories || []).map((p) => ({
    id: p.id,
    productId: p.id,
    productName: p.productName,
    quantity: p.quantity,
  })),
  totalQuotationAmount: 0,
});

export const buildEmptyStageGroup = (stageId, record, stageTemplateMap) => {
  const template = stageTemplateMap[stageId] || {};
  return {
    enquiryStageId: stageId,
    stageName: template.stageName || "",
    stageType: template.stageType,
    priority: record.priority,
    customPricing: false,
    totalQuotationAmount: 0,
    histories: [],
  };
};

// Groups a flat lightweight list into the per-stage shape EnquiryStageHistory
// expects. Used for the first page of results.
export const groupLightweightStageHistory = (list, stageTemplates = []) => {
  const stageTemplateMap = {};
  (stageTemplates || []).forEach((stage) => {
    stageTemplateMap[stage.enquiryStageId] = stage;
  });

  const groupsByStageId = {};

  (list || []).forEach((record) => {
    const stageId = record.stageId;
    if (!groupsByStageId[stageId]) {
      groupsByStageId[stageId] = buildEmptyStageGroup(
        stageId,
        record,
        stageTemplateMap,
      );
    }
    groupsByStageId[stageId].histories.push(
      mapLightweightRecordToHistoryItem(record),
    );
  });

  return Object.values(groupsByStageId);
};

// Merges another page of lightweight records into an already-grouped
// stageHistory list — used for "load more" so earlier pages/columns aren't
// touched or duplicated.
export const appendLightweightStageHistory = (
  existingGroups,
  newRecords,
  stageTemplates = [],
) => {
  const stageTemplateMap = {};
  (stageTemplates || []).forEach((stage) => {
    stageTemplateMap[stage.enquiryStageId] = stage;
  });

  const groupsByStageId = {};
  const seenIds = new Set();
  (existingGroups || []).forEach((group) => {
    groupsByStageId[group.enquiryStageId] = {
      ...group,
      histories: [...group.histories],
    };
    group.histories.forEach((h) => seenIds.add(h.id));
  });

  (newRecords || []).forEach((record) => {
    if (seenIds.has(record.id)) return; // avoid dupes if a page overlaps
    seenIds.add(record.id);
    const stageId = record.stageId;
    if (!groupsByStageId[stageId]) {
      groupsByStageId[stageId] = buildEmptyStageGroup(
        stageId,
        record,
        stageTemplateMap,
      );
    }
    groupsByStageId[stageId].histories.push(
      mapLightweightRecordToHistoryItem(record),
    );
  });

  return Object.values(groupsByStageId);
};

// Maps a single fresh history record (from the per-card detail endpoint)
// into the shape stageHistory items use, so it can be dropped straight
// into state.
export const mapDetailHistoryToStageItem = (freshHistory) => ({
  id: freshHistory.id,
  enquiryId: freshHistory.enquiryId,
  leadName: freshHistory.leadName,
  clientName: freshHistory.clientName,
  assignedTo: freshHistory.assignedTo,
  notes: freshHistory.notes,
  dueAt: freshHistory.dueAt,
  endedAt: freshHistory.endedAt,
  remarks: freshHistory.remarks || "",
  checkListHistories: freshHistory.checkListHistories || [],
  // Endpoint only returns the checklist array itself (no pre-computed
  // counts for a single card), so derive them here.
  completedTasks: (freshHistory.checkListHistories || []).filter(
    (cl) => cl.completed,
  ).length,
  totalTasks: (freshHistory.checkListHistories || []).length,
  productEnquiryHistories: (freshHistory.productEnquiryHistories || []).map(
    (p) => ({
      id: p.id,
      productId: p.productId,
      productName: p.productName,
      skuCode: p.skuCode,
      fulfillmentStatus: p.fulfillmentStatus,
      quantity: p.quantity,
      price: p.price,
      specificationHistories: p.specificationHistories || [],
    }),
  ),
  totalQuotationAmount: freshHistory.totalQuotationAmount || 0,
});

// Replaces exactly one card (by enquiryStageHistoryId) inside stageHistory
// with fresh detail from `/enquiry-stage-history/employee/{id}`, leaving
// every other stage/card untouched.
export const replaceCardInStageHistory = (prevStageHistory, cardDetail) => {
  const freshHistory = cardDetail?.histories?.[0];
  if (!freshHistory) return prevStageHistory;

  const freshItem = mapDetailHistoryToStageItem(freshHistory);

  return prevStageHistory.map((stageGroup) => {
    if (!stageGroup.histories?.some((h) => h.id === freshItem.id)) {
      return stageGroup;
    }
    return {
      ...stageGroup,
      totalQuotationAmount:
        cardDetail.totalQuotationAmount ?? stageGroup.totalQuotationAmount,
      histories: stageGroup.histories.map((h) =>
        h.id === freshItem.id ? { ...h, ...freshItem } : h,
      ),
    };
  });
};

export const calculateSellingPrice = (costPrice, marginPercentage) => {
  if (!costPrice) return 0;
  const margin = marginPercentage || 0;
  return costPrice + (costPrice * margin) / 100;
};
